/**
 * TemplateGridEditor — edit a mode/shift template in the browser.
 *
 * Fixing one officer's afternoon previously meant: download the workbook, find
 * the row, understand run-length encoding, save, upload, review, apply. This is
 * the short path for that — pick a counter, drag across the slots, save.
 *
 * The Excel round-trip stays for bulk work and offline editing; both write the
 * same records and share the same restore points, so an admin can use whichever
 * suits the change.
 *
 * Editing here changes the template that FUTURE roster generations start from.
 * It does not touch a roster that has already been generated, and it does not
 * appear in that roster's version history — only actions on the roster itself
 * (generating, editing, applying a quick action) create a tracked version.
 *
 * Unlike the workbook, this editor stores raw per-slot values — no RLE. The
 * server expands and collapses that for the file; here the user paints cells.
 *
 * Painting is locked to the row (SN) a drag started on — a run of slots is
 * always "the same officer's afternoon", never a diagonal smear across two
 * officers. On touch, painting a run additionally requires a press-and-hold: a
 * quick tap still edits one cell (mirroring a mouse click), but only a
 * deliberate hold arms multi-cell painting, so a finger merely swiping past the
 * grid to scroll can't repaint a row of cells by accident.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiClient } from "@/api/client";
import { useNotification } from "@/contexts/NotificationContext";
import type { OperationMode, ShiftType, TemplateGrid } from "@/types/api";

// A slot is a counter, a break, or a vacancy nobody has assigned yet. The last
// two look the same on paper and mean opposite things to the roster: a break is
// protected, a vacancy is what the initial auto-assign fills. Mirrors
// backend/config.py: BREAK_SLOT / UNASSIGNED_SLOT.
const BREAK = -1;
const UNASSIGNED = 0;
const LONG_PRESS_MS = 320;
/** Finger movement (px) before the hold-to-arm timer is cancelled, i.e. "this is a scroll". */
const TOUCH_CANCEL_DISTANCE = 10;

/** Distinct, stable fill per counter — index-derived so it survives renames. */
function counterColor(index: number): string {
  if (index === BREAK) return "#e2e8f0";      // solid grey — time that is spoken for
  if (index === UNASSIGNED) return "#ffffff"; // blank — nothing here yet
  const hue = (index * 47) % 360;
  return `hsl(${hue} 62% 86%)`;
}

function counterTextColor(index: number): string {
  if (index === BREAK) return "#64748b";
  if (index === UNASSIGNED) return "#cbd5e1";
  const hue = (index * 47) % 360;
  return `hsl(${hue} 70% 26%)`;
}

const tabBtnStyle = (active: boolean): React.CSSProperties => ({
  padding: "4px 12px",
  border: `1px solid ${active ? "#667eea" : "#d1d5db"}`,
  borderRadius: 4,
  background: active ? "#eff6ff" : "white",
  color: active ? "#667eea" : "#374151",
  fontWeight: active ? 600 : 400,
  cursor: "pointer",
  fontSize: 13,
});

interface TemplateGridEditorProps {
  /** Bump to force a reload — e.g. after a sibling Excel upload was applied. */
  reloadSignal?: number;
  /** Fires after a successful "Save grid", so a sibling (the snapshot list) can refresh. */
  onSaved?: () => void;
}

export function TemplateGridEditor({ reloadSignal, onSaved }: TemplateGridEditorProps = {}) {
  const { notifySuccess, notifyError } = useNotification();
  const [mode, setMode] = useState<OperationMode>("arrival");
  const [shift, setShift] = useState<ShiftType>("morning");

  const [grid, setGrid] = useState<TemplateGrid | null>(null);
  const [rows, setRows] = useState<Record<number, number[]>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  /** Counter index the next paint writes; BREAK or UNASSIGNED for the two states. */
  const [brush, setBrush] = useState<number>(BREAK);

  // Mouse: painting.current is set on mousedown, cleared on mouseup anywhere.
  // Locked to paintingRow.current so a wobble off the row mid-drag can't smear
  // into a neighbour's schedule — a run of painted cells is always one officer.
  const painting = useRef(false);
  const paintingRow = useRef<number | null>(null);

  // Touch: a run requires a press-and-hold before it arms (see file header).
  const touchStart = useRef<{ sn: number; slot: number; x: number; y: number } | null>(null);
  const longPressTimer = useRef<number | null>(null);
  const [touchArmed, setTouchArmed] = useState(false);

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimer.current !== null) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setDirty(false);
    apiClient
      .getTemplateGrid(mode, shift)
      .then((g) => {
        setGrid(g);
        setRows(Object.fromEntries(g.rows.map((r) => [r.sn, [...r.slots]])));
        setBrush(g.counters[0]?.index ?? BREAK);
      })
      .catch((err) => notifyError(`Could not load the template: ${err instanceof Error ? err.message : err}`))
      .finally(() => setLoading(false));
  }, [mode, shift, notifyError]);

  useEffect(load, [load]);

  // A sibling upload/rollback/reset changed the template out from under us.
  useEffect(() => {
    if (reloadSignal !== undefined) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadSignal]);

  // A drag can end outside the grid, so release globally rather than on mouseup
  // over a cell — otherwise the brush stays stuck down.
  useEffect(() => {
    const stop = () => {
      painting.current = false;
      paintingRow.current = null;
      clearLongPressTimer();
      touchStart.current = null;
      setTouchArmed(false);
    };
    window.addEventListener("mouseup", stop);
    window.addEventListener("touchcancel", stop);
    return () => {
      window.removeEventListener("mouseup", stop);
      window.removeEventListener("touchcancel", stop);
    };
  }, [clearLongPressTimer]);

  const paint = useCallback((sn: number, slot: number) => {
    setRows((prev) => {
      const current = prev[sn];
      if (!current || current[slot] === brush) return prev;
      const next = [...current];
      next[slot] = brush;
      return { ...prev, [sn]: next };
    });
    setDirty(true);
  }, [brush]);

  // ── Mouse: row-locked drag ──────────────────────────────────────────────
  const handleCellMouseDown = useCallback((sn: number, slot: number) => {
    painting.current = true;
    paintingRow.current = sn;
    paint(sn, slot);
  }, [paint]);

  const handleCellMouseEnter = useCallback((sn: number, slot: number) => {
    if (!painting.current || paintingRow.current !== sn) return;
    paint(sn, slot);
  }, [paint]);

  // ── Touch: press-and-hold arms a row-locked drag; a quick tap edits one cell ──
  const handleCellTouchStart = useCallback((sn: number, slot: number, e: React.TouchEvent) => {
    const t = e.touches[0];
    touchStart.current = { sn, slot, x: t?.clientX ?? 0, y: t?.clientY ?? 0 };
    clearLongPressTimer();
    longPressTimer.current = window.setTimeout(() => {
      paintingRow.current = sn;
      setTouchArmed(true);
      paint(sn, slot);
      // Best-effort — most mobile browsers support it, none require it.
      try { navigator.vibrate?.(12); } catch { /* not supported */ }
    }, LONG_PRESS_MS);
  }, [clearLongPressTimer, paint]);

  const handleCellTouchMove = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;

    if (paintingRow.current === null) {
      // Still waiting on the hold — real movement means this is a scroll, not a
      // paint gesture, so let it go and cancel the pending arm.
      const start = touchStart.current;
      if (start && Math.hypot(touch.clientX - start.x, touch.clientY - start.y) > TOUCH_CANCEL_DISTANCE) {
        clearLongPressTimer();
        touchStart.current = null;
      }
      return;
    }

    // Armed: find the cell under the finger — touchmove's target never changes
    // from the one under touchstart, so this has to go through the DOM.
    const el = document.elementFromPoint(touch.clientX, touch.clientY) as HTMLElement | null;
    const cell = el?.closest<HTMLElement>("[data-sn]");
    if (!cell) return;
    const sn = Number(cell.dataset.sn);
    const slot = Number(cell.dataset.slot);
    if (sn !== paintingRow.current) return; // locked to the row the hold started on
    paint(sn, slot);
  }, [clearLongPressTimer, paint]);

  const handleCellTouchEnd = useCallback(() => {
    clearLongPressTimer();
    // A tap that never armed still edits the one cell it touched — the same
    // outcome a mouse click gives, no hold required.
    if (paintingRow.current === null && touchStart.current) {
      paint(touchStart.current.sn, touchStart.current.slot);
    }
    paintingRow.current = null;
    touchStart.current = null;
    setTouchArmed(false);
  }, [clearLongPressTimer, paint]);

  const labelFor = useMemo(() => {
    const map = new Map<number, string>([[BREAK, "#"], [UNASSIGNED, "(unassigned)"]]);
    grid?.counters.forEach((c) => map.set(c.index, c.label));
    return map;
  }, [grid]);

  const handleSave = async () => {
    if (!grid) return;
    setSaving(true);
    try {
      const result = await apiClient.putTemplateGrid({
        mode,
        shift,
        rows: Object.entries(rows).map(([sn, slots]) => ({ sn: Number(sn), slots })),
      });
      notifySuccess(result.message);
      setDirty(false);
      load();
      onSaved?.();
    } catch (err) {
      notifyError(`Save failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSaving(false);
    }
  };

  const snList = useMemo(
    () => Object.keys(rows).map(Number).sort((a, b) => a - b),
    [rows],
  );

  return (
    <>
      <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap", alignItems: "center" }}>
        {(["arrival", "departure"] as OperationMode[]).map((m) => (
          <button key={m} style={tabBtnStyle(mode === m)} onClick={() => setMode(m)}>
            {m === "arrival" ? "Arrival" : "Departure"}
          </button>
        ))}
        <span style={{ color: "#d1d5db" }}>·</span>
        {(["morning", "night"] as ShiftType[]).map((s) => (
          <button key={s} style={tabBtnStyle(shift === s)} onClick={() => setShift(s)}>
            {s === "morning" ? "Morning" : "Night"}
          </button>
        ))}
        {grid && (
          <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: "auto" }}>
            {grid.source === "custom" ? "custom template" : "built-in defaults"}
          </span>
        )}
      </div>

      <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 8, lineHeight: 1.6 }}>
        Pick a counter below, then click or drag across the grid to assign it —
        on a phone, press and hold a cell to paint a run across that row.
        Choose <strong style={{ color: "#374151" }}># Break</strong> for time off the
        counters, or <strong style={{ color: "#374151" }}>Unassigned</strong> for slots
        the roster should fill in by itself when it is generated.
        Saving keeps the previous version, restorable from the list above.
      </div>

      {/* Brush palette */}
      {grid && (
        <div
          style={{
            display: "flex", flexWrap: "wrap", gap: 4, marginTop: 10,
            maxHeight: 116, overflowY: "auto", padding: 6,
            border: "1px solid #e5e7eb", borderRadius: 6, background: "#fafafa",
          }}
        >
          <button
            onClick={() => setBrush(BREAK)}
            title="Time off the counters — the roster leaves these slots alone"
            style={{
              padding: "3px 10px", borderRadius: 4, fontSize: 11, cursor: "pointer",
              fontWeight: brush === BREAK ? 700 : 500,
              border: `1.5px solid ${brush === BREAK ? "#334155" : "#cbd5e1"}`,
              background: counterColor(BREAK), color: "#475569",
            }}
          >
            # Break
          </button>
          <button
            onClick={() => setBrush(UNASSIGNED)}
            title="Nobody has assigned this slot yet — the roster auto-assigns a counter here"
            style={{
              padding: "3px 10px", borderRadius: 4, fontSize: 11, cursor: "pointer",
              fontWeight: brush === UNASSIGNED ? 700 : 500,
              border: `1.5px solid ${brush === UNASSIGNED ? "#334155" : "#cbd5e1"}`,
              background: counterColor(UNASSIGNED), color: "#475569",
            }}
          >
            Unassigned
          </button>
          {grid.counters.map((c) => (
            <button
              key={c.index}
              onClick={() => setBrush(c.index)}
              title={c.is_motor ? "Motor counter" : "Car counter"}
              style={{
                padding: "3px 10px", borderRadius: 4, fontSize: 11, cursor: "pointer",
                fontWeight: brush === c.index ? 700 : 500,
                border: `1.5px solid ${brush === c.index ? "#334155" : "transparent"}`,
                background: counterColor(c.index),
                color: counterTextColor(c.index),
              }}
            >
              {c.label}{c.is_motor ? " ⍟" : ""}
            </button>
          ))}
        </div>
      )}

      {loading && <div style={{ padding: "14px 0", color: "#9ca3af", fontSize: 13 }}>Loading grid…</div>}

      {grid && !loading && (
        <div
          style={{
            marginTop: 10, overflow: "auto", maxHeight: 420,
            border: "1px solid #e5e7eb", borderRadius: 6,
            WebkitOverflowScrolling: "touch" as React.CSSProperties["WebkitOverflowScrolling"],
            // Scrolling and painting both use touch drag, so scrolling is only
            // suppressed once a hold has actually armed a paint — see file header.
            touchAction: touchArmed ? "none" : "auto",
          }}
          onMouseLeave={() => { painting.current = false; paintingRow.current = null; }}
        >
          <table style={{ borderCollapse: "collapse", fontSize: 10, userSelect: "none" }}>
            <thead>
              <tr>
                <th
                  style={{
                    position: "sticky", left: 0, top: 0, zIndex: 3, background: "#f9fafb",
                    padding: "4px 8px", borderBottom: "1px solid #e5e7eb",
                    fontSize: 11, color: "#6b7280", whiteSpace: "nowrap",
                  }}
                >
                  SN
                </th>
                {grid.time_labels.map((t, i) => (
                  <th
                    key={t}
                    style={{
                      position: "sticky", top: 0, zIndex: 2, background: "#f9fafb",
                      padding: "4px 1px", borderBottom: "1px solid #e5e7eb",
                      borderLeft: i % 4 === 0 ? "1px solid #cbd5e1" : "none",
                      fontSize: 9, color: "#9ca3af", fontWeight: 500,
                      minWidth: 22, whiteSpace: "nowrap",
                    }}
                  >
                    {i % 4 === 0 ? t : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {snList.map((sn) => (
                <tr key={sn}>
                  <td
                    style={{
                      position: "sticky", left: 0, zIndex: 1, background: "white",
                      padding: "2px 8px", borderBottom: "1px solid #f3f4f6",
                      fontWeight: 600, color: "#374151", whiteSpace: "nowrap",
                    }}
                  >
                    SN{sn}
                  </td>
                  {rows[sn].map((value, slot) => (
                    <td
                      key={slot}
                      data-sn={sn}
                      data-slot={slot}
                      onMouseDown={() => handleCellMouseDown(sn, slot)}
                      onMouseEnter={() => handleCellMouseEnter(sn, slot)}
                      onTouchStart={(e) => handleCellTouchStart(sn, slot, e)}
                      onTouchMove={handleCellTouchMove}
                      onTouchEnd={handleCellTouchEnd}
                      title={`SN${sn} · ${grid.time_labels[slot]} · ${labelFor.get(value) ?? value}`}
                      style={{
                        background: counterColor(value),
                        color: counterTextColor(value),
                        borderBottom: "1px solid #fff",
                        borderLeft: slot % 4 === 0 ? "1px solid #cbd5e1" : "1px solid #fff",
                        minWidth: 22, height: 18, textAlign: "center",
                        cursor: "pointer", fontSize: 8, padding: 0,
                        touchAction: "manipulation",
                      }}
                    >
                      {/* '#' in every break slot; a vacancy is blank; a counter
                          labels only the start of its run, so rows stay readable. */}
                      {value === BREAK
                        ? "#"
                        : value !== UNASSIGNED && rows[sn][slot - 1] !== value
                        ? labelFor.get(value)
                        : ""}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12, alignItems: "center" }}>
        {dirty && (
          <span style={{ fontSize: 11, color: "#b45309", marginRight: "auto" }}>
            Unsaved changes
          </span>
        )}
        <button
          onClick={load}
          disabled={saving || loading}
          style={{
            padding: "6px 12px", background: "transparent", color: "#6b7280",
            border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", fontSize: 12,
          }}
        >
          Discard changes
        </button>
        <button
          onClick={() => void handleSave()}
          disabled={saving || loading || !dirty}
          style={{
            padding: "6px 16px", border: "none", borderRadius: 6, color: "white",
            fontSize: 13, fontWeight: 600,
            background: saving || !dirty ? "#9ca3af" : "#667eea",
            cursor: saving || !dirty ? "not-allowed" : "pointer",
          }}
        >
          {saving ? "Saving…" : "Save grid"}
        </button>
      </div>
    </>
  );
}
